import './style.scss'
import { Button, Modal, Upload } from 'antd'
import { RcFile } from 'antd/es/upload'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cmsApi } from '@/api'
import * as Api from '@/api/home'
import IconAdd from '@/assets/icon-add.png'
import { urlSource } from '@/libs/util'
import { DeleteOutlined, LoadingOutlined, StopOutlined } from '@ant-design/icons'

interface IProps {
  open: boolean
  avatar: any
  onCancel: () => void
  updateAvatar: (avatar: any) => void
}

const BackgroundModal: FC<IProps> = (props) => {
  const { t: translate } = useTranslation(['create_video'])
  const { open, avatar, onCancel, updateAvatar } = props
  const [bakgrounds, setbakgrounds] = useState<any[]>([])
  const [alphaUrl, setAlphaUrl] = useState('')
  const [ratio, setRatio] = useState<any>()
  const [bgUrl, setBgUrl] = useState('')
  const [bgId, setBgId] = useState<any>()
  const [loading, setLoading] = useState(false)
  const leftMaxWidth = 480
  const leftHeight = 480

  const leftWidth = useMemo(() => {
    if (ratio) {
      const w = leftHeight * ratio
      return Math.min(leftMaxWidth, w)
    }
    return 0
  }, [ratio])

  useEffect(() => {
    if (open) {
      getBackgrounds()
      setLoading(false)
      if (avatar.bgId) {
        setBgId(avatar.bgId)
        setBgUrl(avatar.bgUrl)
        setAlphaUrl(avatar.alphaUrl)
      } else {
        setBgId(undefined)
        setBgUrl('')
        setAlphaUrl('')
      }
    }
  }, [open])

  const getBackgrounds = async () => {
    const res = await Api.getBackgroundPictures()
    setbakgrounds(res?.list || [])
  }

  const uploadFile = async (file: RcFile) => {
    try {
      const segs = (file.name || '').split(/\./)
      const { upload_url, oss_key, content_type } =
        (await cmsApi.post('upload_url', {
          extension: segs[segs.length - 1],
          media_type: 3
        })) || {}
      if (!upload_url) {
        throw new Error(translate('create_video.upload_failed'))
      }

      setbakgrounds([{}, ...(bakgrounds || [])])

      await cmsApi.upload(upload_url.replace(/^http:\/\//, 'https://').replace('-internal', ''), file, {
        headers: {
          'Content-Type': content_type
        }
      })

      await Api.addBackgroundPictures({
        oss_key
      })
      getBackgrounds()
    } catch (err: any) {
      getBackgrounds()
      console.log(err?.message || err)
    }
  }

  const changeBackground = async (bg: any) => {
    try {
      setLoading(true)
      const res = await Api.removeBackground(avatar.id)

      if (res?.alpha_image_url) {
        setAlphaUrl(res.alpha_image_url)
        setBgUrl(bg.url)
        setBgId(bg.id)
      }
    } finally {
      setLoading(false)
    }
  }

  const deleteBackground = async (id: number, e: any) => {
    e.stopPropagation()
    await Api.deleteBackgroundPictures(id)
    getBackgrounds()
  }

  const onSure = () => {
    updateAvatar({
      ...avatar,
      bgId,
      bgUrl,
      alphaUrl
    })
    onCancel?.()
  }

  return (
    <Modal
      title={translate('create_video.select_background')}
      open={!!open}
      width={leftWidth + 568}
      onCancel={onCancel}
      className="commom-modal background-modal"
      footer={
        <Button type="primary" onClick={onSure}>
          {translate('create_video.confirm')}
        </Button>
      }
      style={{ display: ratio ? 'block' : 'none' }}
    >
      <div className="background-modal-wrappeer">
        <div
          className="left"
          style={{
            width: leftWidth,
            minWidth: leftWidth,
            height: leftHeight
          }}
        >
          {alphaUrl ? (
            <>
              <img
                className="img"
                src={alphaUrl}
                onLoad={(e: any) => {
                  const w = e.target?.naturalWidth
                  const h = e.target?.naturalHeight
                  setRatio(w / h)
                }}
              />
              <img
                className="bg-img"
                src={bgUrl}
                style={{
                  width: ratio >= leftWidth / leftHeight ? '100%' : leftHeight * ratio,
                  height: ratio <= leftWidth / leftHeight ? '100%' : leftWidth / ratio
                }}
              />
            </>
          ) : (
            <>
              <img
                className="img"
                src={urlSource(avatar?.video_url, 'video')}
                onLoad={(e: any) => {
                  const w = e.target?.naturalWidth
                  const h = e.target?.naturalHeight
                  setRatio(w / h)
                }}
              />
            </>
          )}

          {loading && (
            <div className="mask">
              <LoadingOutlined />
              <span>{translate('create_video.replacing_background')}</span>
            </div>
          )}
        </div>
        <div className="right">
          <div className="box-list">
            <div className="box-item add">
              <div className="img-box">
                <Upload
                  accept=".png,.PNG,.jpg,.JPG,.jepg,.JPEG"
                  showUploadList={false}
                  beforeUpload={async (file) => {
                    uploadFile(file)
                    return true
                  }}
                >
                  <div className="upload-box">
                    <img className="icon-add" src={IconAdd} />
                    <div className="text gradient">{translate('create_video.upload_background')}</div>
                  </div>
                </Upload>
              </div>
            </div>

            <div
              className={`box-item blank ${!bgId ? 'actived' : ''}`}
              onClick={() => {
                setBgId(undefined)
                setAlphaUrl('')
                setBgUrl('')
              }}
            >
              <div className={`img-box`}>
                <StopOutlined />
              </div>
            </div>

            {bakgrounds?.map((bg) => (
              <div className={`box-item ${bgId === bg.id ? 'actived' : ''}`} key={bg.id}>
                <div className={`img-box`}>
                  {bg.url ? (
                    <>
                      <img className="img" src={bg.url} onClick={() => changeBackground(bg)} />

                      {!bg.is_public && (
                        <div className="op" onClick={deleteBackground.bind(this, bg.id)}>
                          <DeleteOutlined />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="uploading">
                      <LoadingOutlined />
                      <span>{translate('create_video.uploading')}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default BackgroundModal
